import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../Variables';

import ConfirmationModal from '../../components/ConfirmationModal';
import Pagination from '../../components/Pagination';
import trashIcon from '../../assets/images/trash.svg';
import StarRating from '../../components/StarRating';

function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [deleteReview, setDeleteReview] = useState(null);

  const [refreshReviews, setRefreshReviews] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [limit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [sortBy, setSortBy] = useState("id");
  const [sortOrder, setSortOrder] = useState("asc");

  // Fetch reviews
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setError(null);
        const response = await axios.get(`${API_BASE_URL}/reviews/all-reviews`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          params: {
            offset,
            limit,
            sortBy,
            sortOrder,
          },
        });
        setReviews(response.data.reviews);
        setTotalPages(Math.ceil(response.data.total / limit));
      } catch (err) {
        setError("Failed to load reviews");
      }
    };

    fetchReviews();
  }, [offset, limit, sortBy, sortOrder, refreshReviews]);

  // Open delete review confirmation modal
  const handleDeleteReviewConfirmation = (reviewId) => {
    setOpenConfirmDelete(true);
    setDeleteReview({id: reviewId});
  };

  // Delete review
  const handleDeleteReview = async (reviewId) => {
    try {
      await axios.delete(`${API_BASE_URL}/reviews/admin/${reviewId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });

      setRefreshReviews(!refreshReviews);
      setSuccessMessage('Review deleted successfully.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to delete Review');
    } finally {
      setOpenConfirmDelete(false);
      setDeleteReview(null);
    }
  };

  // Sort reviews
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
    setCurrentPage(1);
    setOffset(0);
  };

  // Render sort indicator
  const renderSortIndicator = (column) => {
    if (sortBy === column) {
      return sortOrder === "asc" ? "▲" : "▼";
    }
    return "";
  };

  return (
    <div className="user-management-tab">
      <h2>Reviews</h2>
      {error && <p className="error">{error}</p>}
      {successMessage && <p className="success">{successMessage}</p>}
      <table className="table">
        <thead>
          <tr>
            <th onClick={() => handleSort("id")}>
              ID {renderSortIndicator("id")}
            </th>
            <th onClick={() => handleSort("User.username")}>
              Username {renderSortIndicator("User.username")}
            </th>
            <th onClick={() => handleSort("Book.title")}>
              Book Title {renderSortIndicator("Book.title")}
            </th>
            <th onClick={() => handleSort("rating")}>
              Rating {renderSortIndicator("rating")}
            </th>
            <th onClick={() => handleSort("review_text")}>
              Review {renderSortIndicator("review_text")}
            </th>
            <th onClick={() => handleSort("date_created")}>
              Date Created {renderSortIndicator("date_created")}
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {reviews.map((review) => (
            <tr key={review.id}>
              <td data-label="ID">{review.id}</td>
              <td data-label="Username">{review.User.username}</td>
              <td data-label="Book Title">{review.Book.title}</td>
              <td data-label="Rating">
                <StarRating rating={review.rating} isReviewing={false} />
              </td>
              <td data-label="Review">{review.review_text}</td>
              <td data-label="Date Created">{new Date(review.date_created).toLocaleDateString()}</td>
              <td data-label="Actions">
                <img src={trashIcon} alt="Delete" onClick={() => handleDeleteReviewConfirmation(review.id)} className='trash-icon'/>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {totalPages > 0 && (
        <Pagination
          totalPages={totalPages}
          setOffset={setOffset}
          limit={limit}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      )}

      {openConfirmDelete && (
        <ConfirmationModal
          onClose={() => setOpenConfirmDelete(false)}
          onConfirm={() => handleDeleteReview(deleteReview.id)}
          title={`Are you sure you want to delete review "${deleteReview.id}"?`}
          message={
            "Doing this will permanently delete the review and cannot be undone."
          }
        />
      )}
    </div>
  );
}

export default ReviewsTab;
